import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { Header } from '../../shared/layout/Header';
import { saveGameResult } from './utils/scoreUtils';
import { getTodaysDailyWord, checkUserPlayedToday, getTimeUntilNextDay, getEasternDate, incrementDailyWordAttempts } from './utils/dailyWordUtils';
import { loadWordLists, isValidWord } from './utils/wordListLoader';
import { generateShareText, copyToClipboard } from './utils/shareUtils';
import {
  trackGameStarted,
  trackGameCompleted,
  trackGuessSubmitted,
  trackGameError
} from '../../core/utils/analytics';
import './WordleGame.css';

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫']
];

export default function WordleGame() {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();

  // Daily word state
  const [dailyWordData, setDailyWordData] = useState(null);
  const [previousScore, setPreviousScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wordListsLoaded, setWordListsLoaded] = useState(false);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Game state
  const [targetWord, setTargetWord] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost', 'completed'
  const [shake, setShake] = useState(false);
  const [letterStates, setLetterStates] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [revealingRow, setRevealingRow] = useState(null);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [shareButtonText, setShareButtonText] = useState('Share Results');

  // LocalStorage key for game state persistence
  const getGameStateKey = (userId) => `wordle_game_${userId}_${getEasternDate()}`;

  // Save game state to localStorage
  const saveGameState = useCallback((state) => {
    if (!currentUser) return;

    try {
      const gameState = {
        guesses: state.guesses || guesses,
        currentGuess: state.currentGuess !== undefined ? state.currentGuess : currentGuess,
        letterStates: state.letterStates || letterStates,
        gameStartTime: state.gameStartTime || gameStartTime,
        gameStatus: state.gameStatus || gameStatus,
        targetWord: state.targetWord || targetWord,
        timestamp: Date.now()
      };

      localStorage.setItem(getGameStateKey(currentUser.uid), JSON.stringify(gameState));
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  }, [currentUser, guesses, currentGuess, letterStates, gameStartTime, gameStatus, targetWord]);

  // Load game state from localStorage
  const loadGameState = useCallback(() => {
    if (!currentUser) return null;

    try {
      const saved = localStorage.getItem(getGameStateKey(currentUser.uid));
      if (!saved) return null;

      const gameState = JSON.parse(saved);

      // Check if saved state is from today (prevent stale data)
      const todayString = getEasternDate();
      const savedKey = getGameStateKey(currentUser.uid);
      if (!savedKey.includes(todayString)) {
        localStorage.removeItem(savedKey);
        return null;
      }

      return gameState;
    } catch (error) {
      console.error('Failed to load game state:', error);
      return null;
    }
  }, [currentUser]);

  // Clear game state from localStorage (after completion)
  const clearGameState = useCallback(() => {
    if (!currentUser) return;

    try {
      localStorage.removeItem(getGameStateKey(currentUser.uid));
    } catch (error) {
      console.error('Failed to clear game state:', error);
    }
  }, [currentUser]);

  // Load word lists on mount
  useEffect(() => {
    const initWordLists = async () => {
      try {
        await loadWordLists();
        setWordListsLoaded(true);
      } catch (error) {
        console.error('Error loading word lists:', error);
      }
    };

    initWordLists();
  }, []);

  // Initialize daily word and check if user already played
  useEffect(() => {
    const initializeGame = async () => {
      try {
        setLoading(true);

        // Wait for word lists to load
        if (!wordListsLoaded) {
          return;
        }

        // Get today's daily word
        const dailyWord = await getTodaysDailyWord();
        setDailyWordData(dailyWord);
        setTargetWord(dailyWord.word); // Always set target word for evaluation

        // Check if user already played today
        if (currentUser) {
          const userScore = await checkUserPlayedToday(currentUser.uid);

          if (userScore) {
            // User already played - show review screen and clear any in-progress state
            setPreviousScore(userScore);
            setShowReviewScreen(true);
            setGameStatus('completed');
            clearGameState(); // Clean up localStorage
          } else {
            // User hasn't played - try to restore in-progress game
            const savedState = loadGameState();

            if (savedState && savedState.targetWord === dailyWord.word) {
              // Restore in-progress game
              setGuesses(savedState.guesses);
              setCurrentGuess(savedState.currentGuess);
              setLetterStates(savedState.letterStates);
              setGameStartTime(savedState.gameStartTime);
              setGameStatus(savedState.gameStatus);
              console.log('✅ Restored in-progress game');
            } else {
              // Start new game
              setGameStartTime(Date.now());
              setGameStatus('playing');

              // Track game started
              trackGameStarted(getEasternDate(), 1);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing game:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeGame();
  }, [currentUser, wordListsLoaded, loadGameState, clearGameState]);

  // Countdown timer for next word
  useEffect(() => {
    if (showReviewScreen) {
      const interval = setInterval(() => {
        setCountdown(getTimeUntilNextDay());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [showReviewScreen]);

  // Evaluate a guess
  const evaluateGuess = useCallback((guess) => {
    const result = [];
    const targetLetters = targetWord.split('');
    const guessLetters = guess.split('');
    const counted = new Array(5).fill(false);
    
    // First pass: mark correct positions (green)
    guessLetters.forEach((letter, i) => {
      if (letter === targetLetters[i]) {
        result[i] = 'correct';
        counted[i] = true;
      }
    });
    
    // Second pass: mark present letters (yellow)
    guessLetters.forEach((letter, i) => {
      if (result[i]) return; // Already marked as correct
      
      const targetIndex = targetLetters.findIndex((l, idx) => 
        l === letter && !counted[idx]
      );
      
      if (targetIndex !== -1) {
        result[i] = 'present';
        counted[targetIndex] = true;
      } else {
        result[i] = 'absent';
      }
    });
    
    return result;
  }, [targetWord]);

  // Handle key press
  const handleKeyPress = useCallback((key) => {
    if (gameStatus !== 'playing') return;
    
    if (key === 'ENTER') {
      if (currentGuess.length !== 5) {
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }

      if (!isValidWord(currentGuess)) {
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return;
      }
      
      const evaluation = evaluateGuess(currentGuess);
      const newGuesses = [...guesses, { word: currentGuess, evaluation }];

      setRevealingRow(guesses.length);
      setTimeout(() => setRevealingRow(null), 1500);

      setGuesses(newGuesses);

      // Update letter states for keyboard
      const newLetterStates = { ...letterStates };
      currentGuess.split('').forEach((letter, i) => {
        const state = evaluation[i];
        if (!newLetterStates[letter] ||
            (state === 'correct') ||
            (state === 'present' && newLetterStates[letter] !== 'correct')) {
          newLetterStates[letter] = state;
        }
      });
      setLetterStates(newLetterStates);

      // Track guess submission
      const correctPositions = evaluation.filter(e => e === 'correct').length;
      const correctLetters = evaluation.filter(e => e === 'correct' || e === 'present').length;
      trackGuessSubmitted(newGuesses.length, correctLetters, correctPositions);

      // Save game state after each guess
      saveGameState({
        guesses: newGuesses,
        currentGuess: '',
        letterStates: newLetterStates
      });
      
      if (currentGuess === targetWord) {
        setTimeout(async () => {
          setGameStatus('won');
          setShowModal(true);

          // Save score to Firestore with daily word data
          if (currentUser && !scoreSaved && gameStartTime) {
            try {
              const completionTimeMs = Date.now() - gameStartTime;
              const todayString = getEasternDate();

              await saveGameResult(currentUser.uid, {
                username: userProfile.username,
                targetWord,
                guesses: newGuesses.map(g => g.word),
                won: true,
                attempts: newGuesses.length,
                completionTimeMs,
                isDaily: true,
                dailyWordDate: todayString
              });

              // Increment daily word attempts counter
              await incrementDailyWordAttempts(todayString);

              // Track game completion (won)
              trackGameCompleted({
                gameDate: todayString,
                won: true,
                attempts: newGuesses.length,
                score: 100,
                timeSpent: Math.round(completionTimeMs / 1000),
                wordLength: targetWord.length
              });

              setScoreSaved(true);
              setPreviousScore({
                guesses: newGuesses.map(g => g.word),
                won: true,
                attempts: newGuesses.length,
                completionTimeMs
              });

              // Clear localStorage after successful save
              clearGameState();
            } catch (error) {
              console.error('Failed to save score:', error);
              trackGameError('save_score', error.message);
            }
          }
        }, 1500);
      } else if (newGuesses.length === 6) {
        setTimeout(async () => {
          setGameStatus('lost');
          setShowModal(true);

          // Save score to Firestore with daily word data
          if (currentUser && !scoreSaved && gameStartTime) {
            try {
              const completionTimeMs = Date.now() - gameStartTime;
              const todayString = getEasternDate();

              await saveGameResult(currentUser.uid, {
                username: userProfile.username,
                targetWord,
                guesses: newGuesses.map(g => g.word),
                won: false,
                attempts: 7, // Lost games count as 7 attempts
                completionTimeMs,
                isDaily: true,
                dailyWordDate: todayString
              });

              // Increment daily word attempts counter
              await incrementDailyWordAttempts(todayString);

              // Track game completion (lost)
              trackGameCompleted({
                gameDate: todayString,
                won: false,
                attempts: 7,
                score: 0,
                timeSpent: Math.round(completionTimeMs / 1000),
                wordLength: targetWord.length
              });

              setScoreSaved(true);
              setPreviousScore({
                guesses: newGuesses.map(g => g.word),
                won: false,
                attempts: 7,
                completionTimeMs
              });

              // Clear localStorage after successful save
              clearGameState();
            } catch (error) {
              console.error('Failed to save score:', error);
              trackGameError('save_score', error.message);
            }
          }
        }, 1500);
      }

      setCurrentGuess('');
    } else if (key === '⌫' || key === 'BACKSPACE') {
      const newGuess = currentGuess.slice(0, -1);
      setCurrentGuess(newGuess);
      // Save partial guess to localStorage
      saveGameState({ currentGuess: newGuess });
    } else if (currentGuess.length < 5 && /^[A-Z]$/.test(key)) {
      const newGuess = currentGuess + key;
      setCurrentGuess(newGuess);
      // Save partial guess to localStorage
      saveGameState({ currentGuess: newGuess });
    }
  }, [currentGuess, gameStatus, guesses, evaluateGuess, letterStates, targetWord, currentUser, scoreSaved, gameStartTime, saveGameState, clearGameState]);

  // Keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        handleKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        handleKeyPress('⌫');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  // Handle modal close - show review screen after game ends
  const handleModalClose = () => {
    setShowModal(false);
    setShowReviewScreen(true);
    setGameStatus('completed');
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle share button click
  const handleShare = async () => {
    if (!previousScore || !dailyWordData) return;

    const scoreGuesses = previousScore.guesses || [];
    const won = previousScore.won;
    const attempts = previousScore.attempts;

    // Recreate evaluations from saved guesses
    const evaluatedGuesses = scoreGuesses.map(guess => ({
      word: guess,
      evaluation: evaluateGuess(guess)
    }));

    const shareText = generateShareText(
      evaluatedGuesses,
      attempts,
      won,
      dailyWordData.dateString
    );

    const success = await copyToClipboard(shareText);

    if (success) {
      setShareButtonText('Copied!');
      setTimeout(() => {
        setShareButtonText('Share Results');
      }, 2000);
    } else {
      setShareButtonText('Failed to copy');
      setTimeout(() => {
        setShareButtonText('Share Results');
      }, 2000);
    }
  };

  // Render review screen (admire puzzle)
  const renderReviewScreen = () => {
    if (!previousScore) return null;

    const scoreGuesses = previousScore.guesses || [];
    const won = previousScore.won;
    const attempts = previousScore.attempts;

    // Recreate evaluations from saved guesses
    const evaluatedGuesses = scoreGuesses.map(guess => ({
      word: guess,
      evaluation: evaluateGuess(guess)
    }));

    return (
      <div className="review-screen">
        <div className="review-container">
          <div className="review-header">
            <h1 className="review-date">{dailyWordData ? formatDate(dailyWordData.dateString) : 'Today'}</h1>
            <p className="review-result">
              {won
                ? `You solved it in ${attempts} ${attempts === 1 ? 'guess' : 'guesses'}.`
                : `Better luck tomorrow! The word was ${targetWord}.`
              }
            </p>
          </div>

          <div className="review-grid">
            {evaluatedGuesses.map((guess, rowIndex) => (
              <div key={rowIndex} className="review-row">
                {guess.word.split('').map((letter, colIndex) => (
                  <div
                    key={colIndex}
                    className={`review-tile ${guess.evaluation[colIndex]}`}
                  >
                    {letter}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="review-actions">
            <button
              className="review-button share"
              onClick={handleShare}
            >
              {shareButtonText}
            </button>
            <button
              className="review-button primary"
              onClick={() => navigate('/leaderboard')}
            >
              View Leaderboard
            </button>
          </div>

          <div className="review-countdown">
            <p>Next puzzle in:</p>
            <div className="countdown-timer">
              <span>{String(countdown.hours).padStart(2, '0')}</span>
              <span>:</span>
              <span>{String(countdown.minutes).padStart(2, '0')}</span>
              <span>:</span>
              <span>{String(countdown.seconds).padStart(2, '0')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render grid
  const renderGrid = () => {
    const rows = [];
    
    for (let i = 0; i < 6; i++) {
      const guess = guesses[i];
      const isCurrentRow = i === guesses.length;
      const isRevealing = revealingRow === i;
      
      const tiles = [];
      for (let j = 0; j < 5; j++) {
        let letter = '';
        let state = '';
        
        if (guess) {
          letter = guess.word[j];
          state = guess.evaluation[j];
        } else if (isCurrentRow && currentGuess[j]) {
          letter = currentGuess[j];
          state = 'filled';
        }
        
        tiles.push(
          <div
            key={j}
            className={`tile ${state} ${isRevealing ? 'reveal' : ''} ${
              isCurrentRow && shake ? 'shake' : ''
            }`}
            style={isRevealing ? { animationDelay: `${j * 0.3}s` } : {}}
          >
            {letter}
          </div>
        );
      }
      
      rows.push(
        <div key={i} className="grid-row">
          {tiles}
        </div>
      );
    }
    
    return rows;
  };

  // Render keyboard
  const renderKeyboard = () => {
    return (
      <div className="keyboard">
        {KEYBOARD_ROWS.map((row, i) => (
          <div key={i} className="keyboard-row">
            {row.map((key) => {
              const state = letterStates[key] || '';
              const isSpecial = key.length > 1;
              
              return (
                <button
                  key={key}
                  className={`key ${state} ${isSpecial ? 'special' : ''}`}
                  onClick={() => handleKeyPress(key)}
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="game-page">
        <Header />
        <div className="game-container">
          <div className="game-loading">
            <div className="spinner"></div>
            <p>Loading today's puzzle...</p>
          </div>
        </div>
      </div>
    );
  }

  // Review screen - user already played today
  if (showReviewScreen) {
    return (
      <div className="game-page">
        <Header />
        <div className="game-container">
          {renderReviewScreen()}
        </div>
      </div>
    );
  }

  // Game screen
  return (
    <div className="game-page">
      <Header />

      <div className="game-container">
        <div className="game-shell">
          <header className="game-hero">
            <div className="hero-content">
              <p className="eyebrow">HU Games • Daily Challenge</p>
              <h1>Howard Wordle</h1>
              <p className="hero-subtitle">
                Daily word challenge! Guess today's 5-letter word in 6 tries. Everyone gets the same word.
                Letters turn green when correct, yellow when in the word but wrong position, and gray when not in the word.
              </p>
            </div>
          </header>

          <section className="wordle-game-section">
            <div className="game-board">
              {renderGrid()}
            </div>

            {renderKeyboard()}
          </section>

          {showModal && (
            <div className="game-modal-overlay" onClick={handleModalClose}>
              <div className="game-modal" onClick={(e) => e.stopPropagation()}>
                <h2>{gameStatus === 'won' ? '🎉 You Won!' : '😔 Game Over'}</h2>
                <p className="modal-word">
                  {gameStatus === 'won'
                    ? `Great job! You guessed it in ${guesses.length} ${guesses.length === 1 ? 'try' : 'tries'}!`
                    : `The word was: ${targetWord}`
                  }
                </p>
                <button className="modal-button" onClick={handleModalClose}>
                  View Results
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
